import { useState } from 'react';
import { AreaChart } from '@/features/ah/charts';
import { fmtNum, fmtTimeNL } from '@/features/ah/utils';
import type { PrijsfavorietenPart } from '../report';
import { fmtDecimal, parseDurationSec, partStatus } from '../utils';
import { CompareStat, type PartLabels } from './PartCompare';

/*
 * 02 — Engagement, two-part layout: the daily rhythm of ONE part at a time
 * (Deel 1 / Deel 2 switch, Deel 2 first) with the games / visitors switch, then
 * the three per-user figures of both parts with the change between them.
 *
 * The parts are shown one at a time rather than overlaid, as designed: they are
 * different weeks of the year with different lengths so far, so a shared x-axis
 * would compare day 3 of one with day 3 of the other and imply more than it can.
 */

type Which = 'previous' | 'current';

/** Days of the part that have happened: a running part is charted up to today, not with a tail of empty days. */
function elapsed(part: PrijsfavorietenPart): number {
  const c = part.campaign;
  return partStatus(c) === 'Lopend' ? Math.max(0, Math.min(c.currentDay, part.days.length)) : part.days.length;
}

export function CompareEngagementSection({ previous, current }: { previous: PrijsfavorietenPart; current: PrijsfavorietenPart }) {
  const [which, setWhich] = useState<Which>('current');
  const [series, setSeries] = useState<'games' | 'visitors'>('games');
  const labels: PartLabels = { previous: previous.campaign.periodLabel, current: current.campaign.periodLabel };

  const part = which === 'current' ? current : previous;
  const c = part.campaign;
  const status = partStatus(c);
  const n = status === 'Gepland' ? 0 : elapsed(part);
  const days = part.days.slice(0, n).map((d) => new Date(d));
  const data = (series === 'games' ? part.series.games : part.series.visitors).slice(0, n);

  // Same "active days" convention as the single-part section: a rate over the
  // days that have data, not over the whole 21-day window.
  const activeDays = Math.max(1, part.series.games.filter((v) => v > 0).length);
  const finished = status === 'Afgerond';
  const over = finished ? ` over ${activeDays} dagen` : '';
  const cur =
    series === 'games'
      ? {
          chartLabel: 'Games',
          color: '#0E4A82',
          total: part.totals.gamesPlayed,
          sub: `Gemiddeld ${fmtNum(Math.round(part.totals.gamesPlayed / activeDays))} games per dag${over}.`,
        }
      : {
          chartLabel: 'Unieke bezoekers',
          color: '#1B6FB8',
          total: part.totals.uniqueVisitors,
          sub: `Gemiddeld ${fmtNum(part.totals.avgVisitorsPerDay)} unieke bezoekers per dag${over}.`,
        };
  const caption = `${c.periodLabel} — ${c.calendarWeeks} · ${finished ? 'afgerond' : status === 'Lopend' ? `dag ${c.currentDay} van ${c.totalDays}` : `start ${c.start}`}`;

  const p = previous.totals;
  const t = current.totals;
  const started = current.campaign.currentDay > 0;
  const val = (s: string) => (started ? fmtTimeNL(s) : '—');

  return (
    <section id="engagement" className="section">
      <div className="section-head">
        <div>
          <div className="eyebrow">02 — Engagement</div>
          <h2>Dagelijks ritme</h2>
        </div>
      </div>

      <div className="card">
        <div className="card-head pf-card-head-wrap">
          <h3>Per dag</h3>
          <div className="pf-toggles">
            <div className="pf-chip-row" role="group" aria-label="Campagnedeel">
              {(['previous', 'current'] as const).map((k) => (
                <button key={k} type="button" className={'preset-chip' + (which === k ? ' active' : '')} aria-pressed={which === k} onClick={() => setWhich(k)}>
                  {labels[k]}
                </button>
              ))}
            </div>
            <div className="pf-chip-row" role="group" aria-label="Reeks">
              <button
                type="button"
                className={'preset-chip' + (series === 'games' ? ' active' : '')}
                aria-pressed={series === 'games'}
                onClick={() => setSeries('games')}
              >
                Games per dag
              </button>
              <button
                type="button"
                className={'preset-chip' + (series === 'visitors' ? ' active' : '')}
                aria-pressed={series === 'visitors'}
                onClick={() => setSeries('visitors')}
              >
                Unieke bezoekers
              </button>
            </div>
          </div>
        </div>
        <div className="trend-block">
          <div className="trend-summary">
            <div className="label">{caption}</div>
            <div className="big">{n > 0 ? fmtNum(cur.total) : '—'}</div>
            <div className="desc">{n > 0 ? cur.sub : `${c.periodLabel} is nog niet begonnen.`}</div>
          </div>
          {n > 0 ? (
            <AreaChart data={data} days={days} color={cur.color} label={`${cur.chartLabel} ${c.periodLabel}`} height={220} />
          ) : (
            <div className="pf-chart-empty">
              Nog geen dagen om te tonen — {c.periodLabel} start op {c.start}.
            </div>
          )}
        </div>
      </div>

      <div className="pf-compare-stats pf-card-gap">
        <CompareStat
          label="Games / gebruiker"
          labels={labels}
          previous={fmtDecimal(p.avgGamesPerPlayer)}
          current={started ? fmtDecimal(t.avgGamesPerPlayer) : '—'}
          prevValue={p.avgGamesPerPlayer}
          curValue={started ? t.avgGamesPerPlayer : null}
          desc={started ? `Gemiddeld ${fmtDecimal(t.avgGamesPerPlayer)} games per gebruiker in ${labels.current}` : `Nog geen gegevens voor ${labels.current}`}
        />
        <CompareStat
          label="Tijd / gebruiker"
          labels={labels}
          previous={fmtTimeNL(p.avgTimePerUser)}
          current={val(t.avgTimePerUser)}
          prevValue={parseDurationSec(p.avgTimePerUser)}
          curValue={started ? parseDurationSec(t.avgTimePerUser) : null}
          desc={started ? `Gemiddeld ${fmtTimeNL(t.avgTimePerUser)} per gebruiker in ${labels.current}` : `Nog geen gegevens voor ${labels.current}`}
        />
        <CompareStat
          label="Tijd / sessie"
          labels={labels}
          previous={fmtTimeNL(p.avgSessionDuration)}
          current={val(t.avgSessionDuration)}
          prevValue={parseDurationSec(p.avgSessionDuration)}
          curValue={started ? parseDurationSec(t.avgSessionDuration) : null}
          desc={started ? `Gemiddeld ${fmtTimeNL(t.avgSessionDuration)} per sessie in ${labels.current}` : `Nog geen gegevens voor ${labels.current}`}
        />
      </div>
    </section>
  );
}
